// TODO: Add Flow annotations and unit tests
// TODO: separate concerns of some methods to make more 'functional' for easier testing and readability
// TODO Extract arrow nav and pagination functionaly into abstract modules
import { MediaQuery } from '../utils/';

/**
 * Usage
 * MARKUP:
 * <div data-js-modules="carousel" data-carousel-breakpoint-max="767" data-items-per-slide="3" ...>
 *      <div>[SLIDE CONTENT]</div> - Nth amount of children (slides)
 * </div>
 */

const BREAKPOINT_DEFS = {
  min: {
    small: 500,
    medium: 768,
    large: 1024
  },
  max: {
    small: 499,
    medium: 767,
    large: 1023
  }
};

const defaultOpts = {
  breakpointMax: BREAKPOINT_DEFS.max.medium,
  breakpointMin: undefined,
  itemsPerSlide: 1
};

const GUTTER = 20;
const ARROW_TIMEOUT = 6000;
const PREFIX = 'js-carousel';
const CSS_PREFIX = 'ps-carousel';
const SELECTORS = {
  module: CSS_PREFIX,
  track: `${CSS_PREFIX}__track`,
  indicators: `ps-slide-indicators`,
  navigationArrows: `ps-arrow-navigation`,
  navigationArrowItem: `ps-arrow-navigation__item`
};

const STATES = {
  active: 'm-active',
  arrowsHidden: 'm-hidden'
};

/**
 * Returns breakpoint number from constant - TODO refactor to make more robust
 * @param {any} direction
 * @param {any} size
 */
const getBreakPoint = (direction, size) => {
  if (
    BREAKPOINT_DEFS.hasOwnProperty(direction) &&
    BREAKPOINT_DEFS[direction].hasOwnProperty(size)
  ) {
    return BREAKPOINT_DEFS[direction][size];
  } else {
    return undefined;
  }
};

export default class Carousel {
  /**
   * Carousel constructor, receives props from `initModules`
   * @param {HTMLElement} $el
   * @param {Object} opts
   */
  constructor($el, opts) {
    this.isSetup = false;

    this.options = Object.assign({}, defaultOpts, opts);

    this.$el = $el;
    this.$slides = [...this.$el.children];
    this.activeIndex = 0;
    this.isMobile = MediaQuery.create({maxWidth: BREAKPOINT_DEFS.max.small}).matches;
  }

  /**
   * Initialises the class
   */
  init() {
    if (this.$slides.length < 2) return;

    this.slideCount = Math.ceil(
      this.$slides.length / this.options.itemsPerSlide
    );

    // Creates a media query condition to add listeners to and query
    this.mq = MediaQuery.create({
      minWidth: getBreakPoint('min', this.options.breakpointMin),
      maxWidth: getBreakPoint('max', this.options.breakpointMax)
    });

    // Set initial active state
    this.setupOrDestroy();

    // listen to media query changes to setup / destroy module
    this.mq.addListener(this.setupOrDestroy.bind(this));

    window.addEventListener('orientationchange', this.setupOrDestroy.bind(this));
  }

  /**
   * Creates the wrapping element which acts as the 'lense' in which the 'track' slides along
   * @returns {HTMLElement}
   */
  createWrapper() {
    const $wrapper = document.createElement('div');
    $wrapper.style.overflow = 'hidden';
    $wrapper.style.width = '100%';
    $wrapper.classList.add(PREFIX);
    $wrapper.classList.add(SELECTORS.module);

    return $wrapper;
  }

  /**
   * Creates indicator markup
   * @returns {HTMLElement}
   */
  createIndicators() {
    const $indicators = document.createElement('div');
    $indicators.classList.add(SELECTORS.indicators);

    for (let i = 0; i < this.slideCount; i++) {
      const $indicator = document.createElement('i');

      $indicator.innerText = i;
      $indicators.appendChild($indicator);
    }

    return $indicators;
  }

  /**
   * Creates indicator markup
   * @returns {HTMLElement}
   */
  createNavigationArrows() {
    const $navigationArrows = document.createElement('div');

    $navigationArrows.classList.add(SELECTORS.navigationArrows);

    ['next', 'previous'].forEach(type => {
      const $arrow = document.createElement('button');
      $arrow.classList.add(SELECTORS.navigationArrowItem);
      $arrow.innerText = type;
      $arrow.setAttribute(`m-${type}`, '');

      $navigationArrows.appendChild($arrow);
    });

    return $navigationArrows;
  }

  /**
   * Queries the media query object to determine wether the component sould be setup or destroyed
   * @returns {Function}
   */
  setupOrDestroy() {
    window.setTimeout(() => this.mq.matches ? this.setup() : this.destroy(), 0)
  }


  /**
   * Sets aria state for the active slide based on the active index
   */
  setAriaStates() {
    this.$slides.forEach((_$slide, index) => {
      _$slide.setAttribute('aria-hidden', this.activeIndex !== index);
    });
  }

  /**
   * Sets active class on active indicator based on active index
   */
  setActiveIndicator() {
    for (const _$indicator of this.$indicators.children) {
      _$indicator.removeAttribute(STATES.active);
    }

    this.$indicators.children[this.activeIndex].setAttribute(STATES.active, '');
  }

  setNavigationArrowsState(isActive) {
    isActive
      ? this.$navigationArrows.removeAttribute(STATES.arrowsHidden)
      : this.$navigationArrows.setAttribute(STATES.arrowsHidden, '');
  }

  /**
   * Publishes the active index
   * @param {Number} index - The index to navigate to
   */
  setactiveIndex(index = 0) {
    // Only allow defensive index setting
    if (index >= 0 && index <= this.slideCount) this.activeIndex = index;
  }

  /**
   * Sets required widths
   */
  setDimensions() {
    // Adding the to prototype for outer calcs
    this.wrapperWidth = this.$wrapper.clientWidth;

    const _indicatorWidth = this.$indicators.children[0].clientWidth;

    // Sets the width of the indicators container equal to the number of indicators with equal spacing between each, adding 40 for the long active state
    this.$indicators.style.width = `${_indicatorWidth *
      (this.slideCount * 2)}px`;

    const spacing = this.options.itemsPerSlide > 1 ? GUTTER : 0;
    const slideWidth = this.wrapperWidth / this.options.itemsPerSlide;

    // Set slides widths
    this.$slides.forEach((_$slide, index) => {
      _$slide.style.flex = `0 0 ${slideWidth}px`;
      _$slide.style.margin = '0';
      _$slide.style.padding = '0';
      _$slide.style.maxWidth = '100vw';

      if (this.options.itemsPerSlide > 1) {
        _$slide.style.flexBasis = `${slideWidth -
          (20 * (this.options.itemsPerSlide - 1)) /
            this.options.itemsPerSlide}px`;
        if (index % this.options.itemsPerSlide)
          _$slide.style.marginLeft = '20px';
      }
    });
  }

  /**
   * Registers initial touch data to be queried against when triggering touchend action
   * @param {Object} e - Event data
   */
  startTouch(e) {
    e.stopPropagation();

    this.touchOrigin = e.targetTouches[0].pageX;
  }

  /**
   * Checks touchend data against touch start data to determine direction to move carousel
   * @param {Object} e - Event data
   */
  triggerTouch(e) {
    const _distance = e.changedTouches[0].pageX - this.touchOrigin;
    const _neutralDistance = Math.abs(_distance);
    const _minDistance = 100;

    if (_neutralDistance > _minDistance) {
      this.navigateSlides(_distance < _minDistance);
    }

    this.touchOrigin = null;
  }

  /**
   * Generates markup and structure to render the carousel
   * TODO: Reduce size!
   */
  setup() {
    /*
        When the module is already setup but device has changed orientation
        we want to only set the dimensions to the new module size
    */
    if (this.$wrapper) this.setDimensions();
    if (this.$indicators) this.setActiveIndicator();

    this.setAriaStates();
    this.moveSlides();

    // Only continue setup if the module has not already been initially setup
    if (this.isSetup) return;

    this.$wrapper = this.createWrapper();
    // Add the wrapper to the DOM
    this.$el.classList.add(SELECTORS.track);
    this.$el.parentNode.insertBefore(this.$wrapper, this.$el);

    this.$indicators = this.createIndicators();
    this.$navigationArrows = this.createNavigationArrows();

    this.$indicators.style.marginLeft = '50%';
    this.$indicators.style.transform = 'translateX(-50%)';

    // Move the module markup inside a wrapper
    this.$navigationArrows.appendChild(this.$el);
    // Add the controls to the DOM
    this.$wrapper.appendChild(this.$navigationArrows);
    // Add the indicators to the DOM
    this.$wrapper.appendChild(this.$indicators);

    this.$el.style.display = 'flex';
    this.$el.style.transition = 'transform ease .3s';
    this.$el.style.flexWrap = 'nowrap';
    this.$el.style.justifyContent = 'flex-start';

    this.setDimensions();
    this.setActiveIndicator();
    this.addEvents();

    this.isSetup = true;
  }

  /**
   * Destroys any additional markup generated by 'setup', removes additional styles to restore original state and deregisters listeners
   */
  destroy() {
    // Only destroy if the module has not already been destroyed
    if (!this.isSetup) return;

    const $parent = this.$wrapper.parentNode;
    // Restore the orginal markup structure
    $parent.insertBefore(this.$el, this.$wrapper);
    $parent.removeChild(this.$wrapper);

    this.$el.classList.remove(SELECTORS.track);

    this.$el.removeAttribute('style');

    for (const _$slide of this.$slides) {
      _$slide.removeAttribute('style');
      _$slide.removeAttribute('aria-hidden');
    }

    this.destroyModuleEvents();

    this.isSetup = false;
  }

  /**
   * Move the 'track' of slides to a new position based on the given index
   */
  moveSlides() {
    // create a transform x axis position from the active index
    let _position = this.activeIndex * this.wrapperWidth;
    const _maxTransition = this.$el.outerWidth;

    _position =
      _position > _maxTransition
        ? _maxTransition
        : _position < 0
          ? 0
          : _position;

    this.$el.style.transform = `translate3d(-${_position}px, 0, 0)`;
  }

  /**
   * Hides navigation arrows whilst user is interacting
   */
  toggleNavigationArrowsOnInteraction = () => {


    if(!this.isMobile) return;

    window.clearTimeout(this.arrowStateTimeOut);

    this.setNavigationArrowsState(false);


    this.arrowStateTimeOut = window.setTimeout(
      this.setNavigationArrowsState.bind(this, true),
      ARROW_TIMEOUT
    );
  };

  /**
   * Given a direction to move slides in sets the next active index and calls state setters
   * @param {Boolean} isNext
   */
  navigateSlides(isNext) {
    this.toggleNavigationArrowsOnInteraction();

    if (
      (isNext && this.activeIndex === this.slideCount - 1) ||
      (!isNext && this.activeIndex === 0)
    )
      return;

    this.setactiveIndex(isNext ? this.activeIndex + 1 : this.activeIndex - 1);
    this.setAriaStates();
    this.setActiveIndicator();
    this.moveSlides();
  }

  onNavigationClick(e) {
    const attr = e.target.attributes;
    const isNavEvent = attr['m-previous'] || attr['m-next'] || null;
    const isNext = e.target.hasAttribute('m-next');

    isNavEvent ? this.navigateSlides(isNext) && e.preventDefault() : null;
  }

  /**
   * Adds event listeners
   */
  addEvents() {
    this.$navigationArrows.addEventListener(
      'click',
      this.onNavigationClick.bind(this)
    );

    if (typeof window.ontouchstart !== 'undefined') {
      this.$wrapper.addEventListener('touchstart', this.startTouch.bind(this));
      this.$wrapper.addEventListener('touchend', this.triggerTouch.bind(this));
    }
  }

  /**
   * Removes event listeners
   */
  destroyModuleEvents() {
    this.$navigationArrows.removeEventListener(
      'click',
      this.onNavigationClick.bind(this)
    );
    this.$wrapper.removeEventListener('touchstart', this.startTouch);
    this.$wrapper.removeEventListener('touchend', this.triggerTouch);
  }
}



// WEBPACK FOOTER //
// ./js/modules/Carousel.js